/**
 * PWA helpers — shared standalone / iOS detection (IB-1).
 *
 * Consolidates the checks that were duplicated across PWAInstallPrompt,
 * IOSNotificationBanner, and (the now-deleted) components/pwa/* banners.
 *
 * @module utils/pwa
 */

/** True when the app is running as an installed PWA (home-screen / standalone). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayMode = !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
  // iOS Safari lacks display-mode; it exposes navigator.standalone instead.
  const iosStandalone =
    'standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true;
  return displayMode || iosStandalone;
}

/**
 * True on iOS Safari/WebKit, where `beforeinstallprompt` does not exist and push
 * only works once the PWA is installed to the home screen. Includes the iPadOS
 * "desktop mode" case (reports MacIntel + touch).
 */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** The Chromium install event. Not in lib.dom, so declared here once. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/*
 * ONBOARD-FIX-1 A2.2 — capture `beforeinstallprompt` AT MODULE LOAD, not in a React effect.
 *
 * Chromium fires this event once, early, and it does not replay. PWAInstallPrompt only
 * started listening from inside a useEffect, so on any load where the event landed before
 * React mounted, it was missed for good — the banner then fell through to its 3-second
 * timeout and told people to open the ⋮ menu and pick "Add to Home screen". That is the
 * two-step, confusing flow the founder reported, and it happened even though the browser
 * was perfectly willing to install in one tap.
 *
 * This module is imported from main.tsx before the app renders, so the listener is live
 * from the first tick and the event is stashed for whenever the banner mounts.
 */
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(e: BeforeInstallPromptEvent) => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Suppress Chrome's own mini-infobar so our on-brand banner is the single ask.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    listeners.forEach((fn) => fn(deferredPrompt as BeforeInstallPromptEvent));
  });

  // Once installed the stashed event is spent and must never be offered again.
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
  });
}

/** The captured install event, if the browser has offered one. */
export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

/** True when a real one-tap install is available right now. */
export function canInstall(): boolean {
  return deferredPrompt !== null;
}

/**
 * Subscribe to the install event. Fires immediately if it was already captured
 * before the subscriber mounted — which is the whole reason this module exists.
 *
 * @returns Unsubscribe function.
 */
export function onInstallPromptAvailable(fn: (e: BeforeInstallPromptEvent) => void): () => void {
  if (deferredPrompt) fn(deferredPrompt);
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Show the native install dialog.
 *
 * @returns 'accepted' | 'dismissed', or 'unavailable' when no event was captured.
 */
export async function showInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const evt = deferredPrompt;
  if (!evt) return 'unavailable';
  try {
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    // The event is single-use; a second prompt() call throws.
    deferredPrompt = null;
    return outcome;
  } catch {
    deferredPrompt = null;
    return 'unavailable';
  }
}
